import { loadBinds, bindLabel } from './GamepadMap.js';
import { loadKeyBinds, keyLabel } from './KeyMap.js';
import { isTouchDevice } from '../render/Quality.js';

/**
 * Radians of turn per CSS pixel of input, at the default sensitivity.
 *
 * Touch is more than three times mouse on purpose. A mouse can be lifted and
 * re-placed, so a long turn is just several strokes; a thumb cannot, so one
 * drag has to cover a useful arc inside one screen width. These values put a
 * 180 degree turn at roughly one full-width swipe on a phone, and at about
 * 1400px of mouse travel on desktop.
 */
const TOUCH_RAD_PER_PX = 0.0095;
const MOUSE_RAD_PER_PX = 0.0022;

/** Slider position that means "unmodified". */
const SENS_BASELINE = 120;

/**
 * The response is deliberately linear.
 *
 * An earlier version scaled gain by the per-frame pixel delta so a flick would
 * turn further than a drag. Measured, it did the opposite: a fast swipe
 * delivers several pointermove events inside one frame, they accumulate into a
 * single large delta, and a capped gain then applies *less* multiplier per
 * pixel than the same distance spread over several frames. A 300px flick came
 * out at 140 degrees against 186 for the identical distance dragged slowly.
 * Speed-based gain needs real timestamps, and it is not worth the
 * unpredictability on a control you aim portals with.
 */

/**
 * One input surface for pointer lock, keyboard and multitouch. Systems read
 * the same fields regardless of device, so nothing downstream knows or cares
 * whether a thumb or a mouse produced the frame.
 */
export class InputManager {
  constructor({ canvas, settings, bus }) {
    this.canvas = canvas;
    this.settings = settings;
    this.bus = bus;
    this.touch = isTouchDevice();

    this.move = { x: 0, z: 0 };
    this.look = { dx: 0, dy: 0 };
    this.jump = false;
    this.sprint = false;
    this._jumpEdge = false;
    this._fire = { a: false, b: false };
    this._respawn = false;
    this._pause = false;
    this._clear = false;

    this.enabled = false;
    this.pointerLocked = false;
    this._lockDrops = 0;
    this._lockAt = 0;
    // Raw (unadjusted) mouse input is the better feel, but its Windows
    // Chrome implementation drops the lock spontaneously on some setups;
    // Linux and macOS keep it. Windows starts on adjusted movement.
    const plat = navigator.userAgentData?.platform ?? navigator.platform ?? '';
    this._unadjusted = !/win/i.test(plat);
    this._padBinds = loadBinds(settings);
    this._keyBinds = loadKeyBinds(settings);
    this._captureCb = null;
    // Menus run on their own low-rate pad poll: gameplay polling lives in
    // sample(), which nobody calls while a menu is up.
    this._menuTimer = setInterval(() => this._menuPoll(), 110);

    this.keys = new Set();
    this._stick = { id: null, ox: 0, oy: 0, x: 0, y: 0 };
    this._lookPointer = { id: null, x: 0, y: 0 };

    this._bind();
  }

  // ------------------------------------------------------------------ setup

  _bind() {
    const d = document;
    d.addEventListener('keydown', this._onKeyDown = (e) => {
      if (e.repeat) return;
      const k = e.code;
      // A rebind capture eats exactly one key; Escape cancels it.
      if (this._keyCapture) {
        const cb = this._keyCapture;
        this._keyCapture = null;
        e.preventDefault();
        e.stopPropagation();
        cb(k === 'Escape' ? null : k);
        return;
      }
      this.keys.add(k);
      // Edge flags only arm during live play: a key pressed on a menu
      // (Escape backing out, E in a text field) must not fire, pause or
      // respawn on the first frame after resume.
      if (!this.enabled) return;
      const b = this._keyBinds;
      if (k === b.jump) { this._jumpEdge = true; e.preventDefault(); }
      if (k === b.respawn) this._respawn = true;
      if (k === b.clear) this._clear = true;
      if (k === 'Escape') this._pause = true;
      if (k === b.fireA) this._fire.a = true;
      if (k === b.fireB) this._fire.b = true;
    });
    d.addEventListener('keyup', this._onKeyUp = (e) => this.keys.delete(e.code));
    window.addEventListener('blur', this._onBlur = () => { this.keys.clear(); this.move.x = this.move.z = 0; });
    this.bus.on('settings:changed', ({ key }) => {
      if (key === 'padBinds' || key === '*') this._padBinds = loadBinds(this.settings);
      if (key === 'keyBinds' || key === '*') this._keyBinds = loadKeyBinds(this.settings);
    });
    window.addEventListener('gamepadconnected', this._onPad = (e) => {
      console.info('[pad] connected:', e.gamepad?.id);
      this.bus.emit('input:gamepad', { id: e.gamepad?.id ?? 'gamepad' });
    });

    // ---- desktop pointer
    // Document-level, capture phase: during unlocked play the freed cursor
    // may sit over ANY element, and every click must still fire and
    // recapture rather than press whatever it happens to be hovering.
    // `enabled` is only true during live play, so menu clicks pass through.
    document.addEventListener('mousedown', this._onMouseDown = (e) => {
      if (!this.enabled || this.touch) return;
      // The shot always counts. Eating the click that recaptures a lost
      // pointer lock reads as "my portal didn't fire", and mid-fall it
      // reads as worse; the camera's aim is valid with or without lock.
      if (e.button === 0) this._fire.a = true;
      if (e.button === 2) this._fire.b = true;
      if (!this.pointerLocked) {
        this.requestLock();
        e.stopPropagation();
      }
      e.preventDefault();
    }, { capture: true });
    this.canvas.addEventListener('contextmenu', this._onCtx = (e) => e.preventDefault());
    d.addEventListener('mousemove', this._onMouseMove = (e) => {
      if (!this.pointerLocked) return;
      this.look.dx += e.movementX || 0;
      this.look.dy += e.movementY || 0;
    });
    d.addEventListener('pointerlockchange', this._onLockChange = () => {
      const wasLocked = this.pointerLocked;
      this.pointerLocked = d.pointerLockElement === this.canvas;
      // Diagnostic breadcrumbs for the lock thief: harmless in production,
      // decisive in a bug report.
      console.info('[lock]', this.pointerLocked ? 'acquired' : 'lost',
        this._selfReleasing ? '(self)' : '(external)');
      // Adaptive raw-input bailout: two external losses shortly after
      // acquisition mean this machine's unadjusted-movement lock is
      // unstable (seen on Windows Chrome); fall back to the adjusted one.
      if (this.pointerLocked) {
        this._lockAt = performance.now();
      } else {
        this._unlockAt = performance.now();   // the relock cooldown clock
      }
      if (!this.pointerLocked && !this._selfReleasing && this._unadjusted) {
        if (performance.now() - (this._lockAt ?? 0) < 10000 && ++this._lockDrops >= 2) {
          this._unadjusted = false;
          console.info('[lock] raw input unstable here; using adjusted movement from now on');
        }
      }
      // Only report a lock the *user* dropped. Releasing it ourselves must
      // not bounce into the pause menu, and neither may a PHANTOM loss:
      // macOS Chrome was seen firing "lost (self)" then a second "lost
      // (external)" for one release, and that ghost paused the game while
      // a fresh lock request was still resolving (the captured-mouse-on-
      // pause-screen report). A loss we did not hold a lock for is noise.
      if (!this.pointerLocked && !this._selfReleasing && wasLocked) {
        this.bus.emit('input:lostLock');
      }
      if (this.pointerLocked) this.bus.emit('input:gotLock');
      this._selfReleasing = false;
    });

    // ---- touch
    this._touchEls = {};
    const el = (id) => document.getElementById(id);
    const stickZone = el('stick-zone');
    const lookZone = el('look-zone');
    this.stickEl = el('stick');

    if (stickZone) {
      stickZone.addEventListener('pointerdown', (e) => {
        if (!this.enabled || this._stick.id !== null) return;
        // Capture can throw if the pointer has already been released or the
        // element is detached; losing capture is survivable, losing the
        // handler is not.
        try { stickZone.setPointerCapture(e.pointerId); } catch (err) { /* keep going */ }
        this._stick.id = e.pointerId;
        this._stick.ox = e.clientX;
        this._stick.oy = e.clientY;
        this._stick.x = 0; this._stick.y = 0;
        this._showStick(true, e.clientX, e.clientY);
        e.preventDefault();
      });
      stickZone.addEventListener('pointermove', (e) => {
        if (e.pointerId !== this._stick.id) return;
        const R = 52;
        let dx = e.clientX - this._stick.ox;
        let dy = e.clientY - this._stick.oy;
        const len = Math.hypot(dx, dy);
        if (len > R) { dx *= R / len; dy *= R / len; }
        // Gentle response curve: a casual half-deflected thumb used to mean
        // HALF the movement (and half the mid-air glide authority, reported
        // as "can't steer to the platform"); ^0.6 lifts the midrange while
        // keeping the rim and the center where they were.
        const mag = Math.min(1, len / R);
        const curved = mag > 0 ? Math.pow(mag, 0.6) / mag : 0;
        this._stick.x = (dx / R) * curved;
        this._stick.y = (dy / R) * curved;
        if (this.stickEl) {
          const knob = this.stickEl.firstElementChild;
          if (knob) knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
        }
        e.preventDefault();
      });
      const endStick = (e) => {
        if (e.pointerId !== this._stick.id) return;
        this._stick.id = null;
        this._stick.x = 0; this._stick.y = 0;
        this._showStick(false);
        const knob = this.stickEl?.firstElementChild;
        if (knob) knob.style.transform = 'translate(-50%, -50%)';
      };
      stickZone.addEventListener('pointerup', endStick);
      stickZone.addEventListener('pointercancel', endStick);
    }

    if (lookZone) {
      lookZone.addEventListener('pointerdown', (e) => {
        if (!this.enabled || this._lookPointer.id !== null) return;
        try { lookZone.setPointerCapture(e.pointerId); } catch (err) { /* keep going */ }
        this._lookPointer.id = e.pointerId;
        this._lookPointer.x = e.clientX;
        this._lookPointer.y = e.clientY;
        e.preventDefault();
      });
      lookZone.addEventListener('pointermove', (e) => {
        if (e.pointerId !== this._lookPointer.id) return;
        this.look.dx += e.clientX - this._lookPointer.x;
        this.look.dy += e.clientY - this._lookPointer.y;
        this._lookPointer.x = e.clientX;
        this._lookPointer.y = e.clientY;
        e.preventDefault();
      });
      const endLook = (e) => {
        if (e.pointerId !== this._lookPointer.id) return;
        this._lookPointer.id = null;
      };
      lookZone.addEventListener('pointerup', endLook);
      lookZone.addEventListener('pointercancel', endLook);
    }

    this._button('btn-jump', () => { this.jump = true; this._jumpEdge = true; }, () => { this.jump = false; });
    this._button('btn-pa', () => { this._fire.a = true; });
    this._button('btn-pb', () => { this._fire.b = true; });
    this._button('btn-reset', () => { this._respawn = true; });
  }

  _button(id, onDown, onUp) {
    const b = document.getElementById(id);
    if (!b) return;
    b.addEventListener('pointerdown', (e) => {
      if (!this.enabled) return;
      b.classList.add('press');
      onDown?.();
      e.preventDefault();
      e.stopPropagation();
    });
    const up = (e) => {
      b.classList.remove('press');
      onUp?.();
      e.preventDefault();
    };
    b.addEventListener('pointerup', up);
    b.addEventListener('pointerleave', up);
    b.addEventListener('pointercancel', up);
  }

  _showStick(on, x = 0, y = 0) {
    if (!this.stickEl) return;
    if (on) {
      this.stickEl.style.left = `${x}px`;
      this.stickEl.style.top = `${y}px`;
      this.stickEl.classList.add('on');
    } else {
      this.stickEl.classList.remove('on');
    }
  }

  // ----------------------------------------------------------------- state

  requestLock() {
    if (this.touch || this.pointerLocked) return;
    let p = null;
    try {
      p = this._unadjusted
        ? this.canvas.requestPointerLock?.({ unadjustedMovement: true })
        : this.canvas.requestPointerLock?.();
    } catch { /* retry below */ }
    if (p?.catch) {
      p.catch((err) => {
        console.info('[lock] request rejected:', err?.name ?? err);
        if (err?.name === 'NotSupportedError') {
          // Raw input unsupported here: fall back to the adjusted lock now.
          try {
            const q = this.canvas.requestPointerLock?.();
            if (q?.catch) q.catch((e2) => console.info('[lock] plain request rejected:', e2?.name ?? e2));
          } catch (e2) { console.info('[lock] plain request threw:', e2?.name ?? e2); }
          return;
        }
        // Chrome rejects re-lock requests after a user-Escape exit with a
        // SecurityError cooldown, and a field trace showed it rejecting a
        // resume WELL past 1.25s from the exit, with the immediate plain
        // fallback rejected in the same breath: the window behaves as if
        // anchored to the failed request, not the unlock. So the one
        // retry always waits a full beat from the REJECTION; the resume
        // click's transient activation (~5s) still vouches for it.
        clearTimeout(this._relockTimer);
        const wait = 1400;
        this._relockTimer = setTimeout(() => {
          if (!this.enabled || this.pointerLocked || this.touch) return;
          try {
            const q = this.canvas.requestPointerLock?.();
            if (q?.catch) q.catch((e2) => console.info('[lock] delayed retry rejected:', e2?.name ?? e2));
          } catch (e2) { console.info('[lock] delayed retry threw:', e2?.name ?? e2); }
        }, wait);
      });
    }
  }

  releaseLock() {
    // A release always cancels any pending relock retry: grabbing the
    // mouse on a pause screen would be the ambush the wiki rules forbid.
    clearTimeout(this._relockTimer);
    this._relockTimer = null;
    if (!document.pointerLockElement) return;
    this._selfReleasing = true;
    document.exitPointerLock?.();
  }

  setEnabled(on) {
    this.enabled = on;
    if (!on) {
      this.keys.clear();
      this._stick.id = null;
      this._stick.x = this._stick.y = 0;
      this._lookPointer.id = null;
      this.jump = false;
      this._showStick(false);
      this.releaseLock();
    }
  }

  /** Read once per frame; edge-triggered flags are cleared by this call. */
  /**
   * Standard-mapping gamepad, polled (the API has no stick events) and
   * merged into the same sample everything else reads: left stick moves,
   * right stick looks, triggers fire, A jumps (held = auto-hop), Start
   * pauses, X respawns, Y clears portals. Deadzoned and square-curved for
   * fine aim. Menus remain touch/mouse; the pad owns gameplay.
   */
  _pollGamepad(out) {
    const pads = navigator.getGamepads?.() ?? [];
    let gp = null;
    for (const p of pads) { if (p && p.connected) { gp = p; break; } }
    const prev = this._gpPrev ?? {};
    this._gpPrev = null;
    if (!gp) return;

    const now = performance.now();
    const dt = Math.min(0.1, (now - (this._gpLast ?? now)) / 1000);
    this._gpLast = now;

    const ax = gp.axes ?? [];

    // Calibrate once per pad: record resting axis values. Any axis idling
    // hard (|rest| > 0.5) is a trigger wearing a stick costume; "standard"
    // mapping is a polite fiction on many pad/browser combos, and reading a
    // resting trigger as right-stick X spins the camera at full speed
    // forever (reported, memorably, as likely to make someone vomit).
    if (!this._gpCal || this._gpCal.id !== gp.id) {
      this._gpCal = { id: gp.id, rest: [...ax] };
    }
    const rest = this._gpCal.rest;
    const centered = [];
    for (let i = 2; i < ax.length; i++) {
      if (Math.abs(rest[i] ?? 0) < 0.5) centered.push(i);
    }
    const rxI = centered[0] ?? -1;
    const ryI = centered[1] ?? -1;

    const curve = (v) => {
      const a = Math.abs(v);
      if (a < 0.18) return 0;
      const n = (a - 0.18) / 0.82;
      return Math.sign(v) * n * n;
    };
    const mx = curve(ax[0] ?? 0);
    const mz = -curve(ax[1] ?? 0);
    const b = (i) => !!gp.buttons?.[i]?.pressed;
    // D-pad moves too (buttons 12-15 on standard pads).
    const dx = (b(15) ? 1 : 0) - (b(14) ? 1 : 0);
    const dz = (b(12) ? 1 : 0) - (b(13) ? 1 : 0);
    if (mx || mz || dx || dz) {
      out.moveX += mx + dx;
      out.moveZ += mz + dz;
      const len = Math.hypot(out.moveX, out.moveZ);
      if (len > 1) { out.moveX /= len; out.moveZ /= len; }
    }

    // Full deflection turns ~200 deg/s at default sensitivity.
    const scale = (this.settings.get('sensitivity') ?? SENS_BASELINE) / SENS_BASELINE;
    const invert = this.settings.get('invertY') ? -1 : 1;
    const rate = 3.5 * scale * dt;
    if (rxI >= 0) out.lookX += curve(ax[rxI] ?? 0) * rate;
    if (ryI >= 0) out.lookY += curve(ax[ryI] ?? 0) * rate * invert;

    // Rebind capture eats the first fresh press whole: nothing else fires.
    if (this._captureCb) {
      for (let i = 0; i < (gp.buttons?.length ?? 0); i++) {
        if (b(i) && !prev[i]) {
          const cb = this._captureCb;
          this._captureCb = null;
          cb(i);
          break;
        }
      }
      this._gpPrev = this._buttonSnapshot(gp);
      return;
    }

    const binds = this._padBinds;
    const held = (action) => (binds[action] ?? []).some((i) => b(i));
    const edge = (action) => (binds[action] ?? []).some((i) => b(i) && !prev[i]);
    if (held('jump')) out.jump = true;
    if (edge('jump')) out.jumpPressed = true;
    if (edge('portalA')) out.fireA = true;
    if (edge('portalB')) out.fireB = true;
    if (edge('respawn')) out.respawn = true;
    if (edge('clearPortals')) out.clearPortals = true;
    if (edge('pause')) out.pause = true;
    this._gpPrev = this._buttonSnapshot(gp);
  }

  _buttonSnapshot(gp) {
    const snap = {};
    for (let i = 0; i < (gp.buttons?.length ?? 0); i++) snap[i] = !!gp.buttons[i]?.pressed;
    return snap;
  }

  /** Next fresh button press goes to cb(index) instead of any action. */
  captureNextButton(cb) { this._captureCb = cb; }

  /**
   * Pad-driven menus: while gameplay input is disabled, d-pad and left
   * stick move focus, A selects, B backs out. Emits bus events; the
   * screens layer owns what focus means.
   */
  _menuPoll() {
    if (this.enabled || document.visibilityState === 'hidden') return;
    const pads = navigator.getGamepads?.() ?? [];
    let gp = null;
    for (const p of pads) { if (p && p.connected) { gp = p; break; } }
    if (!gp) { this._menuPrev = null; return; }
    const prev = this._menuPrev ?? {};
    const b = (i) => !!gp.buttons?.[i]?.pressed;

    // Rebind capture works from menus too; that is where rebinding lives.
    if (this._captureCb) {
      for (let i = 0; i < (gp.buttons?.length ?? 0); i++) {
        if (b(i) && !prev[i]) {
          const cb = this._captureCb;
          this._captureCb = null;
          cb(i);
          break;
        }
      }
      this._menuPrev = this._buttonSnapshot(gp);
      return;
    }

    const ay = gp.axes?.[1] ?? 0;
    const stickDir = ay < -0.6 ? 'up' : ay > 0.6 ? 'down' : null;
    const op = (b(12) && !prev[12]) || (stickDir === 'up' && prev._stick !== 'up') ? 'up'
      : (b(13) && !prev[13]) || (stickDir === 'down' && prev._stick !== 'down') ? 'down'
        : (b(0) && !prev[0]) ? 'select'
          : (b(1) && !prev[1]) ? 'back'
            : null;
    if (op) this.bus.emit('pad:menu', { op });
    const snap = this._buttonSnapshot(gp);
    snap._stick = stickDir;
    this._menuPrev = snap;
  }

  /**
   * One-shot keyboard capture for rebinding: next keydown reports its
   * code (null for Escape = cancelled) and never reaches the game.
   */
  captureNextKey(cb) {
    this._keyCapture = cb;
  }

  /** Whether a gamepad is plugged in at all (hint text, menus). */
  /** The respawn control's on-screen name in the vocabulary of `kind`. */
  respawnLabel(kind) {
    if (kind === 'touch') return 'RST';
    if (kind === 'pad') return bindLabel(this._padBinds, 'respawn');
    return keyLabel(this._keyBinds.respawn);
  }

  get padActive() {
    return (navigator.getGamepads?.() ?? []).some((p) => p && p.connected);
  }

  sample() {
    const k = this.keys;
    let mx = 0, mz = 0;
    const b = this._keyBinds;
    if (k.has(b.forward) || k.has('ArrowUp')) mz += 1;
    if (k.has(b.back) || k.has('ArrowDown')) mz -= 1;
    if (k.has(b.right) || k.has('ArrowRight')) mx += 1;
    if (k.has(b.left) || k.has('ArrowLeft')) mx -= 1;

    if (this._stick.id !== null) {
      mx += this._stick.x;
      mz += -this._stick.y;
    }
    const len = Math.hypot(mx, mz);
    if (len > 1) { mx /= len; mz /= len; }
    this.move.x = mx;
    this.move.z = mz;

    const jumpHeld = this.jump || k.has(b.jump);
    const scale = (this.settings.get('sensitivity') ?? SENS_BASELINE) / SENS_BASELINE;
    const invert = this.settings.get('invertY') ? -1 : 1;

    const perPixel = (this.touch ? TOUCH_RAD_PER_PX : MOUSE_RAD_PER_PX) * scale;

    const out = {
      moveX: mx,
      moveZ: mz,
      lookX: this.look.dx * perPixel,
      lookY: this.look.dy * perPixel * invert,
      jump: jumpHeld,
      jumpPressed: this._jumpEdge,
      sprint: k.has(b.sprint) || k.has('ShiftRight'),
      fireA: this._fire.a,
      fireB: this._fire.b,
      respawn: this._respawn,
      pause: this._pause,
      clearPortals: this._clear,
    };

    if (this.enabled) this._pollGamepad(out);

    this.look.dx = 0;
    this.look.dy = 0;
    this._jumpEdge = false;
    this._fire.a = false;
    this._fire.b = false;
    this._respawn = false;
    this._pause = false;
    this._clear = false;
    return out;
  }

  dispose() {
    document.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('keyup', this._onKeyUp);
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('pointerlockchange', this._onLockChange);
    document.removeEventListener('mousedown', this._onMouseDown, { capture: true });
    window.removeEventListener('blur', this._onBlur);
  }
}
